import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { toast } from "sonner";
import axios from "axios";
import { 
  Book, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  Volume2, 
  VolumeX, 
  Pause, 
  Play,
  Minus,
  Plus,
  X,
  Check,
  CheckCircle2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Highlight colors - mais vibrantes e modernas
const HIGHLIGHT_COLORS = [
  { name: "Amarelo", color: "#fef08a", bg: "bg-yellow-200" },
  { name: "Verde", color: "#bbf7d0", bg: "bg-green-200" },
  { name: "Rosa", color: "#fbcfe8", bg: "bg-pink-200" },
  { name: "Azul", color: "#bfdbfe", bg: "bg-blue-200" },
  { name: "Laranja", color: "#fed7aa", bg: "bg-orange-200" },
];

// Cache global para áudios pré-carregados
const audioCache = new Map();

export default function BibleReaderModal({ 
  open, 
  onOpenChange, 
  readingPlan, 
  version = "nvt",
  onVersionChange 
}) {
  const [loading, setLoading] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [error, setError] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(version);
  
  // Audio state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speechRate, setSpeechRate] = useState(1);
  const [currentVerseIndex, setCurrentVerseIndex] = useState(-1);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioReady, setAudioReady] = useState(false); // Áudio pré-carregado
  const [preloadingAudio, setPreloadingAudio] = useState(false);
  const audioRef = useRef(null);
  const preloadedAudioRef = useRef(null); // Ref para áudio pré-carregado
  const utteranceQueueRef = useRef([]);
  
  // Highlights state
  const [highlights, setHighlights] = useState({});
  const [selectedVerseKey, setSelectedVerseKey] = useState(null);
  
  // Scroll area ref
  const scrollAreaRef = useRef(null);

  // Version names
  const VERSION_NAMES = {
    "nvt": "NVT",
    "nvi": "NVI",
    "acf": "ACF",
    "ntlh": "NTLH"
  };

  useEffect(() => {
    if (open && readingPlan) {
      loadReading();
      loadHighlights();
    }
    return () => {
      stopSpeech();
    };
  }, [open, readingPlan, currentVersion]);

  // Pré-carregar áudio quando capítulo mudar
  useEffect(() => {
    let cancelled = false;
    
    const doPreload = async () => {
      const chapter = chapters[currentChapterIndex];
      if (!chapter?.verses?.length) return;

      // Limitar a 2000 chars para pré-carregamento mais rápido (~10s)
      const text = chapter.verses.map((v, i) => `${v.number || i + 1}. ${v.text}`).join(' ').substring(0, 2000);
      const cacheKey = `${chapter.book_abbrev}-${chapter.chapter}-${text.length}-${currentVersion}`;

      // Se já está no cache, marcar como pronto
      if (audioCache.has(cacheKey)) {
        if (!cancelled) {
          preloadedAudioRef.current = audioCache.get(cacheKey);
          setAudioReady(true);
          setPreloadingAudio(false);
        }
        return;
      }

      // Começar pré-carregamento em background
      if (!cancelled) {
        setPreloadingAudio(true);
        setAudioReady(false);
      }

      try {
        console.log('Iniciando pré-carregamento TTS...');
        const response = await axios.post(`${API}/bible/tts`, {
          text: text,
          voice: "nova",
          speed: 1.0
        }, { timeout: 30000 }); // 30s timeout

        if (cancelled) return;

        if (response.data?.audio_url) {
          const audio = new Audio(response.data.audio_url);
          audio.preload = 'auto';
          
          audioCache.set(cacheKey, audio);
          preloadedAudioRef.current = audio;
          setAudioReady(true);
          console.log('Áudio pré-carregado com sucesso!');
        }
      } catch (err) {
        console.error('Erro ao pré-carregar áudio:', err);
      } finally {
        if (!cancelled) {
          setPreloadingAudio(false);
        }
      }
    };

    if (chapters.length > 0 && chapters[currentChapterIndex]) {
      doPreload();
    }
    
    return () => {
      cancelled = true;
    };
  }, [currentChapterIndex, chapters, currentVersion]);

  // Stop speech when chapter changes
  useEffect(() => {
    stopSpeech();
    setSelectedVerseKey(null);
    setAudioReady(false);
    // Reset scroll to top when chapter changes
    if (scrollAreaRef.current) {
      const scrollViewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollViewport) {
        scrollViewport.scrollTop = 0;
      }
    }
  }, [currentChapterIndex]);

  const loadReading = async () => {
    setLoading(true);
    setError(null);
    setChapters([]);
    setCurrentChapterIndex(0);
    try {
      const response = await axios.post(`${API}/bible/reading`, {
        reading: readingPlan,
        version: currentVersion
      });
      const chaptersData = response.data.chapters || [];
      setChapters(chaptersData);
    } catch (err) {
      setError("Erro ao carregar leitura bíblica");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionChange = (newVersion) => {
    setCurrentVersion(newVersion);
    if (onVersionChange) {
      onVersionChange(newVersion);
    }
  };

  const loadHighlights = async () => {
    try {
      const response = await axios.get(`${API}/bible/highlights`);
      const highlightsData = response.data || [];
      const highlightsMap = {};
      highlightsData.forEach(h => {
        highlightsMap[h.verse_key] = h.color;
      });
      setHighlights(highlightsMap);
    } catch (err) {
      console.error("Error loading highlights:", err);
    }
  };

  const saveHighlight = async (verseKey, color) => {
    try {
      // Optimistic update
      setHighlights(prev => ({ ...prev, [verseKey]: color }));
      setSelectedVerseKey(null);
      
      await axios.post(`${API}/bible/highlights`, {
        verse_key: verseKey,
        color: color
      });
      toast.success("Destaque salvo!");
    } catch (err) {
      // Revert on error
      setHighlights(prev => {
        const newHighlights = { ...prev };
        delete newHighlights[verseKey];
        return newHighlights;
      });
      console.error("Erro ao salvar destaque:", err);
      toast.error("Erro ao salvar destaque");
    }
  };

  const removeHighlight = async (verseKey) => {
    const previousColor = highlights[verseKey];
    try {
      // Optimistic update
      setHighlights(prev => {
        const newHighlights = { ...prev };
        delete newHighlights[verseKey];
        return newHighlights;
      });
      setSelectedVerseKey(null);
      
      await axios.delete(`${API}/bible/highlights/${encodeURIComponent(verseKey)}`);
      toast.success("Destaque removido!");
    } catch (err) {
      // Revert on error
      if (previousColor) {
        setHighlights(prev => ({ ...prev, [verseKey]: previousColor }));
      }
      toast.error("Erro ao remover destaque");
    }
  };

  // ============ AUDIO FUNCTIONS ============
  
  const stopSpeech = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentVerseIndex(-1);
    setAudioProgress(0);
    utteranceQueueRef.current = [];
  };

  const pauseSpeech = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPaused(true);
    } else if ('speechSynthesis' in window) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  const resumeSpeech = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPaused(false);
    } else if ('speechSynthesis' in window) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  };

  const speakChapterWithOpenAI = async () => {
    const chapter = chapters[currentChapterIndex];
    if (!chapter?.verses?.length) return;

    // Se já tem áudio pré-carregado, usar imediatamente!
    if (audioReady && preloadedAudioRef.current) {
      const audio = preloadedAudioRef.current;
      audioRef.current = audio;
      
      // Reset para o início
      audio.currentTime = 0;
      
      audio.ontimeupdate = () => {
        if (audio.duration) {
          setAudioProgress((audio.currentTime / audio.duration) * 100);
        }
      };
      
      audio.onended = () => {
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentVerseIndex(-1);
        setAudioProgress(100);
      };
      
      audio.onerror = () => {
        toast.error("Erro no áudio. Usando voz do navegador...");
        speakChapterWithBrowser();
      };
      
      audio.playbackRate = speechRate;
      
      try {
        await audio.play();
        setIsPlaying(true);
        setCurrentVerseIndex(0);
        return;
      } catch (err) {
        console.error("Erro ao tocar áudio pré-carregado:", err);
        // Fallback para carregar novamente
      }
    }

    // Se não tem pré-carregado, carregar agora (fallback)
    const text = chapter.verses.map((v, i) => `${v.number || i + 1}. ${v.text}`).join(' ');
    const limitedText = text.substring(0, 4000);
    
    setLoadingAudio(true);
    setAudioProgress(0);
    
    try {
      const response = await axios.post(`${API}/bible/tts`, {
        text: limitedText,
        voice: "nova",
        speed: speechRate
      });
      
      if (response.data?.audio_url) {
        const audio = new Audio(response.data.audio_url);
        audioRef.current = audio;
        
        audio.ontimeupdate = () => {
          if (audio.duration) {
            setAudioProgress((audio.currentTime / audio.duration) * 100);
          }
        };
        
        audio.onended = () => {
          setIsPlaying(false);
          setIsPaused(false);
          setCurrentVerseIndex(-1);
          setAudioProgress(100);
        };
        
        audio.onerror = () => {
          toast.error("Erro no áudio. Usando voz do navegador...");
          speakChapterWithBrowser();
        };
        
        audio.playbackRate = speechRate;
        await audio.play();
        setIsPlaying(true);
        setCurrentVerseIndex(0);
      }
    } catch (err) {
      console.error("OpenAI TTS error:", err);
      toast.info("Usando voz do navegador");
      speakChapterWithBrowser();
    } finally {
      setLoadingAudio(false);
    }
  };

  const speakChapterWithBrowser = () => {
    if (!('speechSynthesis' in window)) {
      toast.error("Seu navegador não suporta síntese de voz");
      return;
    }

    const chapter = chapters[currentChapterIndex];
    if (!chapter?.verses?.length) return;

    stopSpeech();
    setIsPlaying(true);

    const voices = window.speechSynthesis.getVoices();
    const ptVoice = voices.find(v => v.lang.startsWith('pt')) || voices[0];

    chapter.verses.forEach((verse, index) => {
      const utterance = new SpeechSynthesisUtterance(verse.text);
      utterance.lang = 'pt-BR';
      utterance.rate = speechRate;
      if (ptVoice) utterance.voice = ptVoice;

      utterance.onstart = () => {
        setCurrentVerseIndex(index);
        setAudioProgress((index / chapter.verses.length) * 100);
      };

      utterance.onend = () => {
        if (index === chapter.verses.length - 1) {
          setIsPlaying(false);
          setCurrentVerseIndex(-1);
          setAudioProgress(100);
        }
      };

      utteranceQueueRef.current.push(utterance);
      window.speechSynthesis.speak(utterance);
    });
  };

  const speakChapter = () => {
    if (isPlaying && !isPaused) {
      pauseSpeech();
      return;
    }

    if (isPaused) {
      resumeSpeech();
      return;
    }

    speakChapterWithOpenAI();
  };

  const adjustSpeed = (delta) => {
    const newRate = Math.max(0.5, Math.min(2, speechRate + delta));
    setSpeechRate(newRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  };

  // Handle verse click for highlighting
  const handleVerseClick = (verseKey) => {
    if (selectedVerseKey === verseKey) {
      setSelectedVerseKey(null);
    } else {
      setSelectedVerseKey(verseKey);
    }
  };

  const currentChapter = chapters[currentChapterIndex];
  const hasNext = currentChapterIndex < chapters.length - 1;
  const hasPrev = currentChapterIndex > 0;

  const goNext = () => {
    if (hasNext) {
      stopSpeech();
      setCurrentChapterIndex(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (hasPrev) {
      stopSpeech();
      setCurrentChapterIndex(prev => prev - 1);
    }
  };

  const goToChapter = (index) => {
    stopSpeech();
    setCurrentChapterIndex(index);
  };

  const getVerseKey = (chapter, verseNum) => {
    return `${chapter.book_abbrev || chapter.book_name}-${chapter.chapter}-${verseNum}`;
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open) stopSpeech();
      onOpenChange(open);
    }}>
      <DialogContent className="max-w-2xl h-[90vh] sm:h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-4 pt-4 pb-2 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Book className="h-5 w-5 text-primary" />
              <DialogTitle className="text-base sm:text-lg">Leitura Bíblica</DialogTitle>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={currentVersion}
                onChange={(e) => handleVersionChange(e.target.value)}
                className="text-sm px-2 py-1.5 rounded-md border bg-background hover:bg-accent cursor-pointer font-medium"
                data-testid="bible-version-selector"
              >
                <option value="nvt">NVT</option>
                <option value="nvi">NVI</option>
                <option value="acf">ACF</option>
                <option value="ntlh">NTLH</option>
              </select>
              {chapters.length > 1 && (
                <span className="text-xs text-muted-foreground hidden sm:inline">
                  {currentChapterIndex + 1}/{chapters.length}
                </span>
              )}
            </div>
          </div>
          {readingPlan && (
            <p className="text-xs text-muted-foreground mt-1">
              {readingPlan} • {VERSION_NAMES[currentVersion]}
            </p>
          )}
        </DialogHeader>

        {/* Audio Controls - Modernizado */}
        {currentChapter && (
          <div className="px-3 py-2 bg-gradient-to-r from-primary/5 to-primary/10 border-y shrink-0">
            <div className="flex items-center gap-2">
              {/* Play/Pause Button */}
              <Button
                variant={isPlaying ? "default" : "outline"}
                size="sm"
                onClick={speakChapter}
                disabled={loadingAudio}
                className={`h-9 w-9 p-0 rounded-full shrink-0 relative ${
                  audioReady && !isPlaying ? "ring-2 ring-green-500 ring-offset-1" : ""
                }`}
                data-testid="bible-audio-btn"
              >
                {loadingAudio ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isPlaying && !isPaused ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4 ml-0.5" />
                )}
              </Button>
              
              {/* Stop Button */}
              {isPlaying && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={stopSpeech}
                  className="h-8 w-8 p-0 rounded-full text-destructive hover:text-destructive shrink-0"
                >
                  <VolumeX className="h-4 w-4" />
                </Button>
              )}

              {/* Progress Bar */}
              <div className="flex-1 mx-2">
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-300 rounded-full"
                    style={{ width: `${audioProgress}%` }}
                  />
                </div>
              </div>

              {/* Speed Controls */}
              <div className="flex items-center gap-1 bg-muted/50 rounded-full px-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => adjustSpeed(-0.25)}
                  disabled={speechRate <= 0.5}
                  className="h-7 w-7 p-0 rounded-full"
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="text-xs font-medium w-8 text-center">{speechRate}x</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => adjustSpeed(0.25)}
                  disabled={speechRate >= 2}
                  className="h-7 w-7 p-0 rounded-full"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>
            
            {/* Status do áudio */}
            <div className="flex items-center justify-center gap-1 mt-1">
              {preloadingAudio && (
                <p className="text-xs text-muted-foreground animate-pulse flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Preparando áudio...
                </p>
              )}
              {audioReady && !isPlaying && !preloadingAudio && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Áudio pronto
                </p>
              )}
              {loadingAudio && (
                <p className="text-xs text-muted-foreground animate-pulse">
                  Carregando áudio...
                </p>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <p className="text-red-500 mb-4">{error}</p>
              <Button variant="outline" onClick={loadReading}>
                Tentar Novamente
              </Button>
            </div>
          </div>
        ) : chapters.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <p className="text-muted-foreground">Nenhuma leitura encontrada</p>
          </div>
        ) : currentChapter ? (
          <>
            {/* Chapter Title */}
            <div className="px-4 py-2 bg-muted/30 border-b shrink-0">
              <h2 className="text-lg font-bold text-primary">
                {currentChapter.book_name} {currentChapter.chapter}
              </h2>
              <p className="text-xs text-muted-foreground">
                Toque em um versículo para destacar
              </p>
            </div>

            {/* Verses */}
            <ScrollArea className="flex-1 px-4" ref={scrollAreaRef}>
              <div className="py-4 space-y-1">
                {currentChapter.verses?.map((verse, index) => {
                  const verseKey = getVerseKey(currentChapter, verse.number);
                  const highlightColor = highlights[verseKey];
                  const isCurrentlyReading = index === currentVerseIndex;
                  const isSelected = selectedVerseKey === verseKey;

                  return (
                    <div key={verse.number} className="relative">
                      <div 
                        className={`leading-relaxed p-2 rounded-lg cursor-pointer transition-all ${
                          isCurrentlyReading 
                            ? "bg-primary/20 ring-2 ring-primary" 
                            : isSelected
                            ? "ring-2 ring-primary/50"
                            : "hover:bg-muted/50 active:bg-muted"
                        }`}
                        style={highlightColor && !isSelected ? { 
                          backgroundColor: highlightColor,
                        } : {}}
                        onClick={() => handleVerseClick(verseKey)}
                        data-testid={`verse-${verse.number}`}
                      >
                        <span className="text-primary font-bold text-sm mr-2">
                          {verse.number}
                        </span>
                        <span 
                          className={`${verse.jesus_words ? "text-red-600 dark:text-red-400 font-medium" : "text-foreground"}`}
                        >
                          {verse.text}
                        </span>
                      </div>

                      {/* Highlight Menu - Inline */}
                      {isSelected && (
                        <div className="flex items-center gap-1 mt-1 p-1.5 bg-popover border rounded-lg shadow-lg animate-in fade-in slide-in-from-top-1 duration-200">
                          <span className="text-xs text-muted-foreground px-1 hidden sm:inline">Destacar:</span>
                          {HIGHLIGHT_COLORS.map((c) => (
                            <button
                              key={c.name}
                              className={`w-7 h-7 rounded-full border-2 transition-all hover:scale-110 active:scale-95 ${
                                highlightColor === c.color ? "border-primary ring-2 ring-primary/30" : "border-transparent"
                              }`}
                              style={{ backgroundColor: c.color }}
                              onClick={(e) => {
                                e.stopPropagation();
                                saveHighlight(verseKey, c.color);
                              }}
                              title={c.name}
                            />
                          ))}
                          {highlightColor && (
                            <>
                              <div className="w-px h-5 bg-border mx-1" />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive rounded-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeHighlight(verseKey);
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 ml-auto rounded-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVerseKey(null);
                            }}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Navigation */}
            {chapters.length > 1 && (
              <div className="p-3 border-t flex items-center justify-between shrink-0 bg-background">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goPrev}
                  disabled={!hasPrev}
                  className="h-9"
                  data-testid="bible-prev-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Anterior</span>
                </Button>

                <div className="flex gap-1.5">
                  {chapters.map((_, idx) => (
                    <button
                      key={idx}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        idx === currentChapterIndex
                          ? "bg-primary scale-125"
                          : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                      }`}
                      onClick={() => goToChapter(idx)}
                    />
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={goNext}
                  disabled={!hasNext}
                  className="h-9"
                  data-testid="bible-next-btn"
                >
                  <span className="hidden sm:inline">Próximo</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
